import type { RemoteSyncGateway } from './gateway'
import {
  TransientSyncError,
  type MutationReceipt,
  type OutboxMutation,
  type PullChange,
  type PullPage,
  type ServerEntity,
} from '../../domain/sync'

type StoredReceipt = MutationReceipt & { originalOutcome: MutationReceipt['outcome'] }

function serverKey(entity: Pick<ServerEntity, 'entityType' | 'entityId'>): string {
  return `${entity.entityType}:${entity.entityId}`
}

export type DemoSyncGatewayOptions = {
  connectivityCheck?: () => boolean | Promise<boolean>
}

function defaultConnectivityCheck(): boolean {
  return typeof navigator === 'undefined' || navigator.onLine
}

export class DemoSyncGateway implements RemoteSyncGateway {
  readonly #connectivityCheck: () => boolean | Promise<boolean>
  readonly #entities = new Map<string, ServerEntity>()
  readonly #receipts = new Map<string, StoredReceipt>()
  readonly #changes: PullChange[] = []
  #failPush = false
  #cursor = 0

  constructor(options: DemoSyncGatewayOptions = {}) {
    this.#connectivityCheck = options.connectivityCheck ?? defaultConnectivityCheck
  }

  async #assertConnectivity(): Promise<void> {
    if (!(await this.#connectivityCheck())) {
      throw new TransientSyncError('Mode hors ligne : la contribution reste sur cet appareil.')
    }
  }

  get receiptCount(): number {
    return this.#receipts.size
  }

  failNextPush(): void {
    this.#failPush = true
  }

  replaceServerEntity(entity: ServerEntity): void {
    const complete: ServerEntity = {
      ...structuredClone(entity),
      updatedAtServer: entity.updatedAtServer ?? new Date().toISOString(),
    }
    this.#entities.set(serverKey(complete), complete)
    this.#cursor += 1
    this.#changes.push({
      ...structuredClone(complete),
      cursor: this.#cursor,
      operation: complete.deletedAt ? 'delete' : complete.version === 1 ? 'create' : 'update',
    })
  }

  async pushMutations(mutations: OutboxMutation[]): Promise<MutationReceipt[]> {
    await this.#assertConnectivity()
    if (this.#failPush) {
      this.#failPush = false
      throw new TransientSyncError()
    }
    const receipts: MutationReceipt[] = []
    for (const mutation of mutations) {
      const previousReceipt = this.#receipts.get(mutation.mutationId)
      if (previousReceipt) {
        receipts.push({
          ...structuredClone(previousReceipt),
          outcome: previousReceipt.originalOutcome === 'conflict' ? 'conflict' : 'duplicate',
        })
        continue
      }

      const key = `${mutation.entityType}:${mutation.entityId}`
      const current = this.#entities.get(key)
      const receivedAtServer = new Date().toISOString()
      if (
        (mutation.operation === 'update' || mutation.operation === 'delete') &&
        (current?.version ?? 0) !== mutation.baseVersion
      ) {
        const receipt: StoredReceipt = {
          mutationId: mutation.mutationId,
          outcome: 'conflict',
          originalOutcome: 'conflict',
          resultingVersion: current?.version ?? 0,
          receivedAtServer,
          errorCode: 'VERSION_CONFLICT',
          errorMessage: 'Une version plus récente existe déjà.',
          ...(current ? { serverEntity: structuredClone(current) } : {}),
        }
        this.#receipts.set(mutation.mutationId, receipt)
        receipts.push(structuredClone(receipt))
        continue
      }

      const appliedPayload = mutation.entityType === 'reveal' && typeof mutation.payload.revealRequestedAt === 'string'
        ? { ...mutation.payload, state: 'revealed', revealedAt: receivedAtServer }
        : mutation.payload
      const next: ServerEntity = {
        entityType: mutation.entityType,
        entityId: mutation.entityId,
        tripId: mutation.tripId,
        payload: structuredClone(appliedPayload),
        version: (current?.version ?? 0) + 1,
        deletedAt: mutation.operation === 'delete' ? receivedAtServer : null,
        updatedAtServer: receivedAtServer,
      }
      this.#entities.set(key, next)
      this.#cursor += 1
      this.#changes.push({
        ...structuredClone(next),
        cursor: this.#cursor,
        operation: mutation.operation,
      })
      const receipt: StoredReceipt = {
        mutationId: mutation.mutationId,
        outcome: 'applied',
        originalOutcome: 'applied',
        resultingVersion: next.version,
        receivedAtServer,
        errorCode: null,
        errorMessage: null,
      }
      this.#receipts.set(mutation.mutationId, receipt)
      receipts.push(structuredClone(receipt))
    }
    return Promise.resolve(receipts)
  }

  async pullChanges(cursor: number, limit: number): Promise<PullPage> {
    await this.#assertConnectivity()
    const changes = this.#changes.filter((change) => change.cursor > cursor).slice(0, limit)
    const nextCursor = changes.at(-1)?.cursor ?? cursor
    return {
      changes: structuredClone(changes),
      nextCursor,
      hasMore: this.#changes.some((change) => change.cursor > nextCursor),
    }
  }
}
